import { forwardRef, type ButtonHTMLAttributes, type ReactNode } from "react";
import { Link, type LinkProps } from "react-router";
import { cx } from "../lib/cx";
import { Spinner } from "./icons";

export type ButtonVariant = "primary" | "brand" | "secondary" | "ghost" | "danger" | "danger-soft";
export type ButtonSize = "sm" | "md" | "lg";

const base =
  "relative inline-flex select-none items-center justify-center gap-2 whitespace-nowrap font-semibold " +
  "transition-[background-color,box-shadow,color,transform] duration-150 ease-out " +
  "focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-brand-500 " +
  "active:translate-y-px disabled:pointer-events-none disabled:opacity-55 aria-disabled:pointer-events-none aria-disabled:opacity-55";

const variants: Record<ButtonVariant, string> = {
  // The main action on a screen is brand orange, as on hosterr.com.
  primary: "bg-brand-600 text-white shadow-[var(--hx-shadow-brand)] hover:bg-brand-700",
  brand: "bg-brand-600 text-white shadow-[var(--hx-shadow-brand)] hover:bg-brand-700",
  secondary:
    "bg-surface text-fg shadow-[inset_0_0_0_1px_var(--hx-line-strong),0_1px_2px_rgba(15,23,42,.05)] hover:shadow-[inset_0_0_0_1px_var(--hx-line-hover),0_1px_2px_rgba(15,23,42,.06)] hover:bg-canvas",
  ghost: "text-body hover:bg-hover hover:text-fg",
  // Danger is a fixed red-orange (--hx-danger), not a brand shade, so an agency's accent never makes "Delete" look like "Save".
  danger: "bg-[var(--hx-danger)] text-white shadow-[var(--hx-shadow-btn)] hover:bg-[var(--hx-danger-hover)]",
  "danger-soft":
    "bg-surface text-bad shadow-[inset_0_0_0_1px_var(--hx-bad-line)] hover:bg-bad-bg hover:shadow-[inset_0_0_0_1px_var(--hx-bad)]",
};

const sizes: Record<ButtonSize, string> = {
  sm: "h-8 rounded-[7px] px-3 text-[13px]",
  md: "h-9.5 rounded-[var(--radius-control)] px-4 text-[14px]",
  lg: "h-11 rounded-[9px] px-5 text-[15px]",
};

const iconOnly: Record<ButtonSize, string> = {
  sm: "h-8 w-8 rounded-[7px]",
  md: "h-9.5 w-9.5 rounded-[var(--radius-control)]",
  lg: "h-11 w-11 rounded-[9px]",
};

export function buttonClass(variant: ButtonVariant = "secondary", size: ButtonSize = "md", extra?: string) {
  return cx(base, variants[variant], sizes[size], extra);
}

type Common = {
  variant?: ButtonVariant;
  size?: ButtonSize;
  leading?: ReactNode;
  trailing?: ReactNode;
};

export type ButtonProps = ButtonHTMLAttributes<HTMLButtonElement> &
  Common & {
    loading?: boolean;
    /** Text announced while loading, e.g. "Creating site". */
    loadingText?: string;
  };

export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  { variant = "secondary", size = "md", leading, trailing, loading, loadingText, className, children, disabled, type, ...rest },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      className={buttonClass(variant, size, className)}
      disabled={disabled || loading}
      aria-busy={loading || undefined}
      {...rest}
    >
      {loading ? <Spinner size={size === "sm" ? 14 : 16} /> : leading}
      {loading && loadingText ? loadingText : children}
      {!loading && trailing}
    </button>
  );
});

/** An icon-only button. `label` is required: it becomes the accessible name. */
export const IconButton = forwardRef<
  HTMLButtonElement,
  Omit<ButtonHTMLAttributes<HTMLButtonElement>, "aria-label"> & { label: string; variant?: ButtonVariant; size?: ButtonSize }
>(function IconButton({ label, variant = "ghost", size = "md", className, children, type, ...rest }, ref) {
  return (
    <button
      ref={ref}
      type={type ?? "button"}
      aria-label={label}
      className={cx(base, variants[variant], iconOnly[size], "px-0", className)}
      {...rest}
    >
      {children}
    </button>
  );
});

/** A router link styled as a button. */
export function ButtonLink({
  variant = "secondary",
  size = "md",
  leading,
  trailing,
  className,
  children,
  ...rest
}: LinkProps & Common) {
  return (
    <Link className={buttonClass(variant, size, className)} {...rest}>
      {leading}
      {children}
      {trailing}
    </Link>
  );
}

/** An external link styled as a button. Opens in a new tab. */
export function ButtonAnchor({
  variant = "secondary",
  size = "md",
  leading,
  trailing,
  className,
  children,
  ...rest
}: React.AnchorHTMLAttributes<HTMLAnchorElement> & Common) {
  return (
    <a className={buttonClass(variant, size, className)} target="_blank" rel="noreferrer noopener" {...rest}>
      {leading}
      {children}
      {trailing}
    </a>
  );
}
